import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input-v16';
import { environment } from 'src/app/environments/environments';

interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

interface Organization {
  organizationName: string;
  organizationID: string;
}

@Component({
  selector: 'app-delete-my-account-public',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatIconModule,
    NgxMatIntlTelInputComponent,
    MatFormFieldModule,
  ],
  templateUrl: './delete-my-account-public.component.html',
  styleUrls: ['./delete-my-account-public.component.scss'],
})
export class DeleteMyAccountPublicComponent implements OnInit, OnDestroy {
  @ViewChild('phoneInput') phoneInput: any;
  // Form management
  verificationFormGroup: FormGroup;
  hasVerified: boolean = false;
  // UI state management
  currentStep:
    | 'initial'
    | 'verification'
    | 'otp'
    | 'policy'
    | 'confirmation'
    | 'success' = 'initial';

  // Phone verification
  phoneNumber = '';
  countryCode = '+91';
  isMobileValid = false;
  isMobileExists = false;
  organizations: Organization[] = [];
  filteredOrgs: Organization[] = [];
  selectedOrganization: Organization | null = null;
  orgSearchTerm = '';

  // OTP verification
  otpCode = '';
  otpSent = false;
  otpVerified = false;
  resendCountdown = 0;
  resendTimer: any;

  // Deletion policy
  selectedDeletionPolicy: 'immediate' | '30days' = 'immediate';

  // Final confirmation
  confirmationText = '';
  confirmationChecked = false;
  showDeleteModal = false;

  // Message handling
  message = '';
  messageType: 'success' | 'error' = 'success';

  // Loading states
  isLoading = false;
  isSendingOtp = false;
  isVerifyingOtp = false;
  isDeletingAccount = false;

  // API endpoints
  private readonly API_BASE_URL = environment.apiUrl;
  private readonly endpoints = {
    getAllOrganizations: `${this.API_BASE_URL}GetAllOrganization`,
    sendOtp: `${this.API_BASE_URL}auth/send-otp`,
    verifyOtp: `${this.API_BASE_URL}auth/verify-otp`,
    deleteAccount: `${this.API_BASE_URL}RemoveUserPermanently`,
  };

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router
  ) {
    this.verificationFormGroup = this.fb.group({
      mobile: ['', [Validators.required]],
      organization: ['', Validators.required],
      otp: [
        '',
        [Validators.required, Validators.minLength(4), Validators.maxLength(6)],
      ],
    });
  }

  ngOnInit(): void {
    this.setupFormListeners();
  }

  ngOnDestroy(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }

  private setupFormListeners(): void {
    // Listen for phone number changes
    // this.verificationFormGroup
    //   .get('mobile')
    //   ?.valueChanges.subscribe((value) => {
    //     if (value && value.length >= 10) {
    //       this.checkMobileAndFetchOrganizations(value);
    //     } else {
    //       this.resetMobileState();
    //     }
    //   });
    this.verificationFormGroup
      .get('mobile')
      ?.valueChanges.subscribe((value) => {
        console.log('Mobile control value:', value);
      });
    this.verificationFormGroup
      .get('mobile')
      ?.statusChanges.subscribe((status) => {
        console.log('Mobile control status:', status);
        console.log(
          'Mobile control errors:',
          this.verificationFormGroup.get('mobile')?.errors
        );
      });
  }

  // Custom phone validator
  private phoneValidator(control: any) {
    const phoneRegex = /^[1-9]\d{8,14}$/;
    if (control.value && !phoneRegex.test(control.value.replace(/\s/g, ''))) {
      return { invalidPhone: true };
    }
    return null;
  }

  private isValidPhoneNumber(phone: string): boolean {
    const cleanPhone = phone.replace(/\s/g, '');
    return /^[1-9]\d{8,14}$/.test(cleanPhone);
  }

  private resetMobileState(): void {
    this.isMobileValid = false;
    this.isMobileExists = false;
    this.organizations = [];
    this.filteredOrgs = [];
    this.selectedOrganization = null;
    this.orgSearchTerm = '';
  }

  // Step 1: Check mobile and fetch organizations
  FetchOrganizations(mobile: string): void {
    this.isMobileExists = false;
    this.organizations = [];
    const payload = JSON.stringify({ mobile: mobile });
    const params = new HttpParams().set('data', payload);
    this.http
      .get<any[]>(this.endpoints.getAllOrganizations, { params })
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.hasVerified = true;
          if (response && response.length > 0) {
            this.organizations = response;
            this.filteredOrgs = [...this.organizations];
            this.isMobileExists = true;
            this.showMessage(
              'Organizations found for this mobile number',
              'success'
            );
          } else {
            this.isMobileExists = false;
            this.organizations = [];
            this.showMessage(
              'No organizations found for this mobile number',
              'error'
            );
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error fetching organizations:', error);
          this.isMobileExists = false;
          this.showMessage('Error fetching organizations', 'error');
          this.hasVerified = true;
        },
      });
  }
  private checkMobileAndFetchOrganizations(mobile: string): void {
    if (!this.isValidPhoneNumber(mobile)) {
      this.resetMobileState();
      return;
    }

    this.isMobileValid = true;
    this.isLoading = true;
    this.phoneNumber = this.countryCode + mobile.replace(/\s/g, '');

    const payload = {
      mobile: this.phoneNumber,
    };

    this.http
      .post<ApiResponse>(this.endpoints.getAllOrganizations, payload)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success && response.data && response.data.length > 0) {
            this.organizations = response.data;
            this.filteredOrgs = [...this.organizations];
            this.isMobileExists = true;
            this.showMessage(
              'Organizations found for this mobile number',
              'success'
            );
          } else {
            this.isMobileExists = false;
            this.organizations = [];
            this.showMessage(
              'No organizations found for this mobile number',
              'error'
            );
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error fetching organizations:', error);
          this.isMobileExists = false;
          this.showMessage('Error fetching organizations', 'error');
        },
      });
  }
  verifyMobileNumber(): void {
    if (this.mobileControl?.valid && this.phoneInput) {
      this.isLoading = true;
      this.hasVerified = false;
      this.isMobileExists = false;
      this.organizations = [];
      this.selectedOrganization = null;
      this.orgSearchTerm = '';

      // Get the full phone number including country code
      const fullPhoneNumber = this.phoneInput.value;
      this.phoneNumber = fullPhoneNumber;

      // Call your organization API here
      this.FetchOrganizations(fullPhoneNumber);
    }
  }

  // Organization search and selection
  onOrganizationInput(event: any): void {
    const value = event.target.value;
    this.orgSearchTerm = value;

    if (value) {
      this.filteredOrgs = this.organizations.filter((org) =>
        org.organizationName.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.filteredOrgs = [...this.organizations];
    }

    // Reset selection if search term changes
    if (
      this.selectedOrganization &&
      this.selectedOrganization.organizationName !== value
    ) {
      this.selectedOrganization = null;
    }
  }

  selectOrganization(org: Organization): void {
    this.selectedOrganization = org;
    this.orgSearchTerm = org.organizationName;
    this.verificationFormGroup.patchValue({
      organization: org.organizationName,
    });
    this.filteredOrgs = [];
  }

  // Step 1: Initial delete account button
  handleDeleteAccount(): void {
    this.currentStep = 'verification';
    this.showMessage(
      'Please verify your account to proceed with deletion',
      'success'
    );
  }

  // Step 2: Send OTP for verification
  sendOtpForVerification(): void {
    if (
      !this.verificationFormGroup.get('mobile')?.valid ||
      !this.isMobileExists
    ) {
      this.showMessage('Please enter a valid registered phone number', 'error');
      return;
    }

    if (!this.selectedOrganization) {
      this.showMessage('Please select your organization', 'error');
      return;
    }

    this.isSendingOtp = true;

    const payload = {
      phoneNumber: this.phoneNumber,
    };

    this.http.post<ApiResponse>(this.endpoints.sendOtp, payload).subscribe({
      next: (response) => {
        this.isSendingOtp = false;
        if (response.success) {
          this.otpSent = true;
          this.currentStep = 'otp';
          this.startResendCountdown();
          this.showMessage(
            'OTP sent successfully to your mobile number',
            'success'
          );
        } else {
          this.showMessage(response.message || 'Failed to send OTP', 'error');
        }
      },
      error: (error) => {
        this.isSendingOtp = false;
        console.error('Error sending OTP:', error);
        this.showMessage('Error sending OTP. Please try again.', 'error');
      },
    });
  }

  // Step 3: Verify OTP
  verifyOtp(): void {
    if (!this.verificationFormGroup.get('otp')?.valid) {
      this.showMessage('Please enter a valid OTP', 'error');
      return;
    }

    this.isVerifyingOtp = true;

    const payload = {
      phoneNumber: this.phoneNumber,
      otp: this.verificationFormGroup.get('otp')?.value,
    };

    this.http.post<ApiResponse>(this.endpoints.verifyOtp, payload).subscribe({
      next: (response) => {
        this.isVerifyingOtp = false;
        if (response.success) {
          this.otpVerified = true;
          this.currentStep = 'policy';
          this.showMessage('Account verified successfully', 'success');
        } else {
          this.showMessage(response.message || 'Invalid OTP', 'error');
        }
      },
      error: (error) => {
        this.isVerifyingOtp = false;
        console.error('Error verifying OTP:', error);
        this.showMessage('Error verifying OTP. Please try again.', 'error');
      },
    });
  }

  // Step 4: Select deletion policy
  selectDeletionPolicy(policy: 'immediate' | '30days'): void {
    this.selectedDeletionPolicy = policy;
  }

  proceedToConfirmation(): void {
    if (!this.selectedDeletionPolicy) {
      this.showMessage('Please select a deletion policy', 'error');
      return;
    }
    this.currentStep = 'confirmation';
  }

  // Step 5: Show delete confirmation modal
  showDeleteConfirmation(): void {
    if (!this.confirmationChecked) {
      this.showMessage(
        'Please confirm that you understand the consequences',
        'error'
      );
      return;
    }
    this.showDeleteModal = true;
  }

  // Step 6: Final account deletion
  confirmDeleteAccount(): void {
    if (this.confirmationText.toUpperCase() !== 'DELETE') {
      this.showMessage('Please type "DELETE" exactly to confirm', 'error');
      return;
    }

    this.isDeletingAccount = true;
    this.showDeleteModal = false;

    const payload = {
      mobile: this.phoneNumber,
      organizationId: this.selectedOrganization?.organizationID,
      deletionPolicy: this.selectedDeletionPolicy,
      confirmationText: this.confirmationText,
      timestamp: new Date().toISOString(),
    };

    this.http
      .post<ApiResponse>(this.endpoints.deleteAccount, {
        body: payload,
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .subscribe({
        next: (response) => {
          this.isDeletingAccount = false;
          if (response.success) {
            this.currentStep = 'success';
            this.showMessage(
              'Account deletion initiated successfully',
              'success'
            );
            // Redirect to main route after 5 seconds
            setTimeout(() => {
              this.router.navigate(['/']);
            }, 5000);
          } else {
            this.showMessage(
              response.message || 'Failed to delete account',
              'error'
            );
          }
        },
        error: (error) => {
          this.isDeletingAccount = false;
          console.error('Error deleting account:', error);
          this.showMessage(
            'Error deleting account. Please try again or contact support.',
            'error'
          );
        },
      });
  }

  // Resend OTP functionality
  resendOtp(): void {
    if (this.resendCountdown > 0) return;
    this.sendOtpForVerification();
  }

  private startResendCountdown(): void {
    this.resendCountdown = 30;
    this.resendTimer = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        clearInterval(this.resendTimer);
      }
    }, 1000);
  }

  // Navigation methods
  goBack(): void {
    switch (this.currentStep) {
      case 'verification':
        this.currentStep = 'initial';
        break;
      case 'otp':
        this.currentStep = 'verification';
        break;
      case 'policy':
        this.currentStep = 'otp';
        break;
      case 'confirmation':
        this.currentStep = 'policy';
        break;
    }
    this.clearMessage();
  }

  // Modal methods
  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.confirmationText = '';
  }

  // Utility methods
  private showMessage(message: string, type: 'success' | 'error'): void {
    this.message = message;
    this.messageType = type;
  }

  private clearMessage(): void {
    this.message = '';
  }

  // Form helper methods
  get mobileControl() {
    return this.verificationFormGroup.get('mobile');
  }

  get otpControl() {
    return this.verificationFormGroup.get('otp');
  }

  get organizationControl() {
    return this.verificationFormGroup.get('organization');
  }
}
